import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { TagBlueIcon } from '../../../assets/icons';
import { DashboardService, OrdersService } from '../../../services';
import { classNames, Constants, formatMiles, Socket, SocketEvents } from '../../../utils';
import { Render } from '../../../components';

const Home = () => {
  const { chart1, chart2, currentDate, currentMonth, data } = useData();

  return (
    <main className="flex gap-6 items-start">
      <div className="w-1/3 space-y-6">
        <section className="bg-white shadow-md rounded-2xl p-4 space-y-2">
          <span className="text-2xl font-bold">Estado de ejecución de viajes</span>

          <ReactApexChart
            height={260}
            options={chart1.options}
            series={chart1.series}
            type="bar"
          />
        </section>

        <section className="bg-white shadow-md rounded-2xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center bg-[#EFF3FF] border border-blue px-4 py-0.5 rounded-full">
              <img className="w-4 h-4 mr-2" src={TagBlueIcon} alt="" />
              <span className="text-lg font-bold text-blue">Viajes</span>
            </div>
            <span className="text-xl text-red ml-4">{ currentMonth }</span>
          </div>

          <div className="flex items-center">
            <p className="whitespace-pre-wrap leading-4 font-semibold">Total viajes</p>
            <p className="text-4xl font-bold font-sans mx-4">{ data.trips.total }</p>
          </div>

          <ReactApexChart
            height={260}
            options={chart2.options}
            series={chart2.series}
            type="pie"
          />

          <div className="grid grid-cols-2">
            <div>
              <div className="mt-4 flex items-center">
                <div className="w-3 h-3 rounded-full bg-blue mr-2" />
                <p className="font-bold">Viajes por propios</p>
              </div>
              <p className="text-default font-bold">{ data.trips.own_total }</p>
            </div>

            <div>
              <div className="mt-4 flex items-center">
                <div className="w-3 h-3 rounded-full bg-green mr-2" />
                <p className="font-bold">Viajes por afiliados</p>
              </div>
              <p className="text-default font-bold">{ data.trips.affiliates_total }</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4 font-bold">
              <p className="text-lg mb-1">Viajes por cliente</p>

              {data.trips.series?.clients?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <p className="flex-1 text-blue">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4 font-bold">
              <p className="text-lg mb-1">Viajes por ruta</p>

              {data.trips.series?.routes?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <p className="flex-1 text-blue">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4 font-bold">
              <p className="text-lg mb-1">Viajes por conductor</p>

              {data.trips.series?.drivers?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <p className="flex-1 text-blue">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="flex-1 space-y-2">
        <div className="flex items-center gap-4">
          <span className="text-2xl font-bold">Viajes programados</span>
          <div className="flex items-center bg-[#FFF0E2] border border-orange px-4 py-0.5 rounded-full text-lg">
            <span className="font-bold text-orange">HOY</span>
            <span className="font-bold ml-2">{ currentDate }</span>
          </div>
        </div>

        <div>
          {data.orders.map((item, index) => (
            <ListItem key={index} item={item} />
          ))}

          <Render condition={!data.orders.length}>
            <p className="text-center text-default my-8">Sin datos para este día</p>
          </Render>
        </div>
      </section>
    </main>
  );
}

const ListItem = ({ item }) => {
  return (
    <div className="grid grid-cols-7 gap-2 items-start bg-white rounded-lg p-2 mb-2 text-sm border border-gray6 [&>div>label]:font-semibold [&>div>label]:text-[#8D8D8D]">
      <div
        data-col={item.status}
        className={classNames(
          'text-white px-4 h-full rounded-md font-black text-base flex items-center justify-between',
          `data-[col=1]:bg-[#FFE9E9] data-[col=1]:text-[#ED6161]`,
          `data-[col=2]:bg-[#E7EDFF] data-[col=2]:text-blue`,
          `data-[col=3]:bg-[#FFF9CA] data-[col=3]:text-[#F4CE38]`,
          `data-[col=4]:bg-[#EBFFFE] data-[col=4]:text-[#16DB62]`,
          `data-[col=5]:bg-[#F2F2F2] data-[col=5]:text-[#707070]`,
        )}
      >
        <span>{ item.status_text }</span>
      </div>
      <div>
        <label>Ruta</label>
        <p>{ item.address_reference }</p>
      </div>
      <div>
        <label>Cliente</label>
        <p>{ item.user?.person?.fullName }</p>
      </div>
      <div>
        <label>Conductor</label>
        <p>{ item.driver?.person?.fullName }</p>
      </div>
      <div>
        <label>Salida</label>
        <p>{ moment(item.date).format('h:mma') }</p>
      </div>
      <div>
        <label>Placa</label>
        <p>{ item.vehicle?.plaque }</p>
      </div>
      <div>
        <label>Propietario</label>
        <p>{ item?.vehicle?.owner?.person?.name || 'Maxer' }</p>
      </div>
    </div>
  )
}

const useData = () => {
  const currentMonth = moment().format('MMMM');
  const currentDate = moment().format('DD-MM-YYYY');
  const [data, setData] = useState({
    execution: {
      scheduled: 0,
      finished: 0,
    },
    trips: {
      total: 0,
      own_total: 0,
      affiliates_total: 0,
      series: {
        clients: [],
        routes: [],
        drivers: [],
      },
    },
    orders: [],
  });
  const [chart1, setChart1] = useState({
    series: [],
    options: {
      colors: ['#396AFF', '#16DB62'],
      labels: ['Viajes programados', 'Viajes ejecutados'],
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        zoom: false,
      },
      dataLabels: {
        enabled: true,
      },
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 12,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      grid: {
        borderColor: '#e5e7eb',
        yaxis: {
          lines: { show: true }
        },
        xaxis: {
          lines: { show: true }
        }
      },
      yaxis: {
        tickAmount: 1,
        labels: {
          style: {
            colors: '#9ca3af',
            fontSize: '14px',
          }
        }
      },
      xaxis: {
        labels: {
          show: false,
        },
      },
      fill: {
        opacity: 1
      },
      legend: {
        show: true
      },
    },
  });
  const [chart2, setChart2] = useState({
    series: [],
    options: {
      colors: ['#396AFF', '#16DB62'],
      labels: ['Viajes por propios', 'Viajes por afiliados'],
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '16px',
        },
      },
      legend: {
        show: false,
      },
    },
  });

  const fetchData = async () => {
    try {
      const orders = await OrdersService.moderator.findAll({ page: 1, perPage: Constants.PER_PAGE, search: '', status: '', plaque: '', route: '' });
      const response = await DashboardService.supervisor.findAll();
      setData({ orders: orders.data, ...response });
      setChart1(s => ({
        ...s,
        series: [{
          data: [
            Number(response.execution?.scheduled || 0),
            Number(response.execution?.finished || 0),
          ],
        }],
      }));

      setChart2(s => ({
        ...s,
        series: [
          Number(response.trips?.own_total || 0),
          Number(response.trips?.affiliates_total || 0),
        ],
      }));

    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchData();

    // Activa los sockets necesarios
    Socket.on(SocketEvents.ROUTE.FLOW.PENDING, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.REJECTED, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.ACCEPTED, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.ALERT_UNACCEPTED, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.CONFIRMED, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.ALERT_UNCONFIRMED, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.INITIATED, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.ALERT_UNINITIATED, () => fetchData());
    Socket.on(SocketEvents.ROUTE.FLOW.FINISHED, () => fetchData());

    return () => {
      Socket.off(SocketEvents.ROUTE.FLOW.PENDING, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.REJECTED, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.ACCEPTED, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.ALERT_UNACCEPTED, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.CONFIRMED, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.ALERT_UNCONFIRMED, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.INITIATED, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.ALERT_UNINITIATED, () => fetchData());
      Socket.off(SocketEvents.ROUTE.FLOW.FINISHED, () => fetchData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    chart1,
    chart2,
    currentDate,
    currentMonth,
    data,
  };
}

export default Home;
